import React, { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Dropdown from 'react-bootstrap/Dropdown';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { Link } from 'react-router-dom';
import ConversationDetailsSkeleton from './ConversationDetailsSkeleton';

// Parse "Mar 30, 2026 04:04 PM" format and extract date part
const getDateFromModifiedAt = (modifiedAt) => {
    if (!modifiedAt) return 'Unknown';
    // Extract just the date part: "Mar 30, 2026"
    const parts = modifiedAt.split(' ');
    if (parts.length >= 3) {
        return `${parts[0]} ${parts[1]} ${parts[2]}`;
    }
    return modifiedAt;
};

// Parse "Mar 30, 2026 04:04 PM" format and extract time part
const getTimeFromModifiedAt = (modifiedAt) => {
    if (!modifiedAt) return '';
    // Extract time part: "04:04 PM"
    const parts = modifiedAt.split(' ');
    if (parts.length >= 5) {
        return `${parts[3]} ${parts[4]}`;
    }
    return '';
};

const groupMessagesByDate = (messages) => {
    if (!Array.isArray(messages)) return {};
    return messages
        .filter((message) => message && message.message)
        .reduce((groups, message) => {
        const date = getDateFromModifiedAt(message.modifiedAt);
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(message);
        return groups;
    }, {});
};

// Format time - handles both "04:04 PM" format and legacy "HH:MM:SS" format
function formatTime(modifiedAt) {
    if (!modifiedAt) return '';
    // If it's the new format "Mar 30, 2026 04:04 PM", extract time
    const timeStr = getTimeFromModifiedAt(modifiedAt);
    if (timeStr) return timeStr;
    // Fallback for legacy format
    return modifiedAt;
}

function formatDateLabel(dateString) {
    if (!dateString || dateString === 'Unknown') return dateString;
    
    // Parse "Mar 30, 2026" format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
        return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
    } else {
        return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
}

const ConversationList = ({ smsData, handleSendMessage, getConversationDetails, conversationDetailsLoading, conversationDetails, handleDeleteConversation }) => {
    const groupedMessages = groupMessagesByDate(smsData);
    const dates = Object.keys(groupedMessages);

    const [charCount, setCharCount] = useState(0);
    const [message, setMessage] = useState('');
    const messagesEndRef = useRef(null);
    const [maxCount, setMaxCount] = useState(false);
    const [show, setShow] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        getConversationDetails();
        setShow(true);
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        if (handleDeleteConversation) {
            handleDeleteConversation();
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleInputChange = (event) => {
        const inputValue = event.target.value;
        if (inputValue.length <= 450) {
            setMessage(inputValue);
            setCharCount(inputValue.length);
            setMaxCount(false);
        } else {
            setMaxCount(true);
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [smsData]);

    const sendMessage = () => {
        if (message.trim() !== '') {
            handleSendMessage(message);
            setMessage('');
            setCharCount(0);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const TextWithLinks = ({ text, linkColor = 'text-primary' }) => {
        if (!text) return null;

        const urlPattern = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(urlPattern).map((part, index) => {
            if (urlPattern.test(part)) {
                return (
                    <span
                        key={index}
                        className={`${linkColor} text-decoration-underline`}
                        style={{ cursor: 'default' }}
                    >
                        {part}
                    </span>
                );
            }
            return part;
        });
        return <>{parts}</>;
    };

    const getInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0)?.toUpperCase() || '';
        const last = lastName?.charAt(0)?.toUpperCase() || '';
        return first + last || '?';
    };

    return (
        <div className="card shadow-sm border-0 d-flex flex-column" style={{ height: 'calc(100vh - 200px)' }}>
            {/* Chat Header */}
            <div className="card-header bg-white border-bottom py-3" style={{ flexShrink: 0 }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <div 
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{ 
                                width: '45px', 
                                height: '45px',
                                backgroundColor: '#e8f4fd'
                            }}
                        >
                            <span className="fw-bold" style={{ color: '#0d6efd' }}>
                                {getInitials(smsData[0].customerFirstName, smsData[0].customerLastName)}
                            </span>
                        </div>
                        <div>
                            <h6 className="mb-0 fw-bold" style={{ color: '#212529' }}>
                                {smsData[0].customerFirstName} {smsData[0].customerLastName}
                            </h6>
                            {smsData[0].customerDob && (
                                <small className="text-muted">
                                    <i className="bi bi-calendar3 me-1"></i>
                                    {smsData[0].customerDob}
                                </small>
                            )}
                        </div>
                    </div>
                    <Dropdown align="end">
                        <Dropdown.Toggle 
                            variant="light" 
                            size="sm" 
                            className="rounded-circle border-0 dropdown-toggle-hide-caret"
                            style={{ width: '36px', height: '36px', padding: '0' }}
                        >
                            <i className="bi bi-three-dots-vertical"></i>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item onClick={handleShow}>
                                <i className="bi bi-person-circle me-2"></i>
                                User Info
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={handleDeleteClick} className="text-danger">
                                <i className="bi bi-trash me-2"></i>
                                Delete Conversation
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            {/* Messages Area */}
            <div 
                className="card-body message-area" 
                style={{ 
                    overflowY: 'auto', 
                    flex: '1 1 auto',
                    minHeight: 0,
                    backgroundColor: '#f8f9fa'
                }}
            >
                <div className="d-flex flex-column">
                    {dates.map(date => (
                        <div key={date}>
                            {/* Date Separator */}
                            <div className="d-flex justify-content-center my-3">
                                <span className="badge bg-white text-muted border px-3 py-2 rounded-pill shadow-sm">
                                    {formatDateLabel(date)}
                                </span>
                            </div>

                            {/* Messages */}
                            {groupedMessages[date].map((msg, index) => (
                                <div
                                    key={index}
                                    className={`d-flex mb-3 ${msg.messageType === 'OUTGOING' ? 'justify-content-end' : 'justify-content-start'}`}
                                >
                                    <OverlayTrigger
                                        placement={msg.messageType === 'OUTGOING' ? 'left' : 'right'}
                                        overlay={
                                            <Tooltip id={`tooltip-${msg.id || index}`}>
                                                <div className="text-start small">
                                                    <div>{msg.status || 'Unknown'}</div>
                                                    <div>{msg.modifiedAt}</div>
                                                </div>
                                            </Tooltip>
                                        }
                                    >
                                        <div
                                            className={`px-3 py-2 rounded-3 position-relative ${
                                                msg.messageType === 'OUTGOING' 
                                                    ? 'bg-primary text-white' 
                                                    : 'bg-white border shadow-sm'
                                            }`}
                                            style={{ maxWidth: '70%', minWidth: '80px' }}
                                        >
                                            <div className={`mb-1 ${msg.messageType === 'OUTGOING' ? 'text-white' : 'text-dark'}`}>
                                                <TextWithLinks 
                                                    text={msg.message} 
                                                    linkColor={msg.messageType === 'OUTGOING' ? 'text-white' : 'text-primary'} 
                                                />
                                            </div>
                                            <div className={`d-flex align-items-center justify-content-end gap-1 ${
                                                msg.messageType === 'OUTGOING' ? 'text-white-50' : 'text-muted'
                                            }`} style={{ fontSize: '0.7rem' }}>
                                                <span>{formatTime(msg.modifiedAt)}</span>
                                                {msg.messageType === 'OUTGOING' && (
                                                    msg.status === "DELIVERED" 
                                                        ? <i className="bi bi-check2-all"></i>
                                                        : msg.status === "ACCEPTED"
                                                            ? <i className="bi bi-check2"></i>
                                                            : <i className="bi bi-exclamation-circle"></i>
                                                )}
                                            </div>
                                        </div>
                                    </OverlayTrigger>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div ref={messagesEndRef} />
            </div>

            {/* Message Input Area */}
            <div className="card-footer bg-white border-top p-3" style={{ flexShrink: 0 }}>
                {maxCount && (
                    <div className="alert alert-warning py-2 px-3 mb-2 small">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Maximum 450 characters allowed
                    </div>
                )}
                <div className="d-flex gap-2">
                    <div className="flex-grow-1">
                        <textarea 
                            className="form-control border-0 bg-light" 
                            rows="2" 
                            value={message} 
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Type your message..."
                            style={{ resize: 'none' }}
                        />
                    </div>
                    <button 
                        className="btn btn-primary align-self-end px-4" 
                        onClick={sendMessage}
                        disabled={!message.trim()}
                    >
                        <i className="bi bi-send-fill"></i>
                    </button>
                </div>
                <div className="d-flex justify-content-end mt-2">
                    <small className={`${charCount > 400 ? 'text-warning' : 'text-muted'}`}>
                        {charCount}/450
                    </small>
                </div>
            </div>

            {/* Details Modal */}
            <Modal show={show} onHide={handleClose} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="h5">
                        <i className="bi bi-person-lines-fill me-2 text-primary"></i>
                        Contact Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {conversationDetailsLoading ? (
                        <ConversationDetailsSkeleton rows={2}/>
                    ) : (
                        <div className="list-group list-group-flush">
                            {conversationDetails && conversationDetails.map((detail, index) => (
                                <div key={index} className="list-group-item px-0 py-3 border-bottom">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h6 className="mb-1 fw-semibold">
                                                {detail.firstName} {detail.lastName}
                                            </h6>
                                            <small className="text-muted">
                                                <i className="bi bi-hash me-1"></i>
                                                {detail.customerId}
                                            </small>
                                        </div>
                                        {detail.customerDob && (
                                            <span className="badge bg-light text-muted">
                                                <i className="bi bi-calendar3 me-1"></i>
                                                {detail.customerDob}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteConfirm} onHide={handleDeleteCancel} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title>
                        <i className="bi bi-exclamation-triangle text-danger me-2"></i>
                        Delete Conversation
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        Are you sure you want to delete this conversation? This will permanently delete all messages with <strong>{smsData[0]?.customerFirstName} {smsData[0]?.customerLastName}</strong>.
                    </p>
                    <p className="text-muted small mt-2 mb-0">
                        <i className="bi bi-info-circle me-1"></i>
                        This action cannot be undone.
                    </p>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={handleDeleteCancel}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm}>
                        <i className="bi bi-trash me-2"></i>
                        Delete Conversation
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .message-area::-webkit-scrollbar {
                    width: 6px;
                }
                .message-area::-webkit-scrollbar-track {
                    background: transparent;
                }
                .message-area::-webkit-scrollbar-thumb {
                    background: #dee2e6;
                    border-radius: 6px;
                }
                .message-area::-webkit-scrollbar-thumb:hover {
                    background: #adb5bd;
                }
                .dropdown-toggle-hide-caret::after {
                    display: none !important;
                }
            `}</style>
        </div>
    );
};

export default ConversationList;
